import { useEffect, useState } from 'react';
import { Eye, EyeOff, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { Term } from '../../components/admin/AdminGlossary';

type Tab = 0 | 1 | 2 | 3 | 4 | 5 | 6;
const TABS = ['Geral', 'Integrações', 'Notificações', 'LGPD', 'Canais', 'Zona de Risco', 'Interface'];

function useSetting(key: string, defaultVal: string) {
  const [value, setValue] = useState(defaultVal);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    supabase.from('platform_settings').select('value').eq('key', key).maybeSingle()
      .then(({ data }) => { if (data?.value !== undefined) setValue(String(data.value)); setLoaded(true); });
  }, [key]);
  async function save(v: string) {
    setValue(v);
    await supabase.from('platform_settings').upsert({ key, value: v, description: key }, { onConflict: 'key' });
  }
  return [value, save, loaded] as const;
}

function SaveButton({ onClick, saving }: { onClick: () => void; saving: boolean }) {
  return (
    <button className="btn btn-primary btn-sm" onClick={onClick} disabled={saving}
      style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
      <Check size={14} /> {saving ? 'Salvando...' : 'Salvar'}
    </button>
  );
}

function SecretField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const [show, setShow] = useState(false);
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input type={show ? 'text' : 'password'} className="form-input" value={value} onChange={e => onChange(e.target.value)} style={{ fontFamily: 'monospace', fontSize: '13px', flex: 1 }} />
        <button className="btn btn-ghost btn-sm" onClick={() => setShow(v => !v)}>
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

export function AdminSettingsPage() {
  const { profile }   = useAuth();
  const [tab, setTab] = useState<Tab>(0);
  const [saving, setSaving] = useState(false);

  // Geral settings
  const [siteName, setSiteName]   = useSetting('site_name', 'Professya');
  const [tagline,  setTagline]    = useSetting('site_tagline', '3.000 anos de sabedoria astrológica');
  const [maintenance, setMaintenance] = useSetting('maintenance_mode', 'false');
  const [regOpen,  setRegOpen]    = useSetting('registration_open', 'true');
  const [trialDays, setTrialDays] = useSetting('free_trial_days', '0');
  const [affiliateCommission, setAffiliateCommission] = useSetting('affiliate_commission_brl', '10');

  // Integrations
  const [stripeKey,     setStripeKey]     = useSetting('stripe_secret_key_masked', '');
  const [gtmId,         setGtmId]         = useSetting('gtm_id', '');
  const [anthropicKey,  setAnthropicKey]  = useSetting('anthropic_api_key_masked', '');
  const [resendKey,     setResendKey]     = useSetting('resend_api_key_masked', '');

  // Notifications
  const [adminEmail, setAdminEmail] = useSetting('admin_notification_email', '');
  const [notifyReg,  setNotifyReg]  = useSetting('notify_new_user', 'true');
  const [notifyPay,  setNotifyPay]  = useSetting('notify_payment', 'true');
  const [notifyChurn,setNotifyChurn]= useSetting('notify_churn_risk', 'false');

  // LGPD
  const [retentionDays, setRetentionDays] = useSetting('data_retention_days', '395');
  const [cookieBanner,  setCookieBanner]  = useSetting('cookie_banner_enabled', 'true');
  const [dpoEmail,      setDpoEmail]      = useSetting('dpo_email', '');
  const [exportEmail,   setExportEmail]   = useState('');
  const [exporting,     setExporting]     = useState(false);

  // Channel contact settings
  const [chWhatsapp,  setChWhatsapp]  = useSetting('channel_whatsapp',      '');
  const [chInstagram, setChInstagram] = useSetting('channel_instagram',     'professya');
  const [chTiktok,    setChTiktok]    = useSetting('channel_tiktok',        'professya');
  const [chYoutube,   setChYoutube]   = useSetting('channel_youtube',       '');
  const [chEmailSup,  setChEmailSup]  = useSetting('channel_email_support', 'suporte@professya.com');

  // Zone of danger
  const [resetInput,    setResetInput]    = useState('');
  const [dangerConfirm, setDangerConfirm] = useState('');

  // Interface, admin nav section order
  const DEFAULT_NAV_ORDER = ['Geral', 'Financeiro', 'Usuários', 'Plataforma', 'Conteúdo', 'Compliance', 'Sistema'];
  const [navOrder, setNavOrder] = useState<string[]>(DEFAULT_NAV_ORDER);
  const [navOrderLoaded, setNavOrderLoaded] = useState(false);
  const [savingNavOrder, setSavingNavOrder] = useState(false);

  useEffect(() => {
    supabase.from('platform_settings').select('value').eq('key', 'admin_nav_section_order').maybeSingle()
      .then(({ data }) => {
        if (data?.value) {
          try {
            const parsed = JSON.parse(String(data.value));
            if (Array.isArray(parsed) && parsed.length > 0) setNavOrder(parsed);
          } catch { /* keep default */ }
        }
        setNavOrderLoaded(true);
      });
  }, []);

  async function saveNavOrder() {
    setSavingNavOrder(true);
    await supabase.from('platform_settings').upsert(
      { key: 'admin_nav_section_order', value: JSON.stringify(navOrder), description: 'admin_nav_section_order' },
      { onConflict: 'key' }
    );
    setSavingNavOrder(false);
  }

  function moveNavSection(index: number, direction: 'up' | 'down') {
    const newOrder = [...navOrder];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    setNavOrder(newOrder);
  }

  async function saveGeral() {
    setSaving(true);
    await Promise.all([setSiteName(siteName), setTagline(tagline), setMaintenance(maintenance), setRegOpen(regOpen), setTrialDays(trialDays), setAffiliateCommission(affiliateCommission)]);
    setSaving(false);
  }

  async function saveChannels() {
    setSaving(true);
    await Promise.all([setChWhatsapp(chWhatsapp), setChInstagram(chInstagram), setChTiktok(chTiktok), setChYoutube(chYoutube), setChEmailSup(chEmailSup)]);
    setSaving(false);
  }

  async function saveIntegrations() {
    setSaving(true);
    await Promise.all([setStripeKey(stripeKey), setGtmId(gtmId), setAnthropicKey(anthropicKey), setResendKey(resendKey)]);
    setSaving(false);
  }

  async function saveNotifications() {
    setSaving(true);
    await Promise.all([setAdminEmail(adminEmail), setNotifyReg(notifyReg), setNotifyPay(notifyPay), setNotifyChurn(notifyChurn)]);
    setSaving(false);
  }

  async function saveLgpd() {
    setSaving(true);
    await Promise.all([setRetentionDays(retentionDays), setCookieBanner(cookieBanner), setDpoEmail(dpoEmail)]);
    setSaving(false);
  }

  async function exportUserData() {
    if (!exportEmail) return;
    setExporting(true);
    const { data: user } = await supabase.from('profiles').select('id').eq('email', exportEmail).maybeSingle();
    if (user) {
      const { data } = await supabase.functions.invoke('admin-export-user-data', { body: { userId: user.id } });
      const blob = new Blob([JSON.stringify(data?.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `user_data_${exportEmail}.json`; a.click();
      URL.revokeObjectURL(url);
    }
    setExporting(false);
  }

  async function clearForecastCache() {
    await supabase.from('platform_settings').delete().like('key', 'forecast_%');
  }

  async function resetAllPrices() {
    if (resetInput !== 'RESETAR') return;
    await supabase.from('product_prices').update({ price_brl: 0, price_usd: 0 }).neq('id', '');
    await supabase.from('admin_audit_log').insert({ admin_id: profile?.id, action: 'reset_all_prices', entity_type: 'product_prices', new_value: { action: 'all_set_free' } });
    setResetInput('');
  }

  return (
    <div>
      <AdminPageHeader title="Configurações" objective="Configurações globais da plataforma: informações do site, integrações de pagamento, notificações, conformidade legal e canais de contato. A aba 'Zona de Risco' contém ações irreversíveis, use com extrema cautela." />

      <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1.5rem', overflowX: 'auto' }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i as Tab)}
            className={tab === i ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            style={{ whiteSpace: 'nowrap' }}>
            {t === 'Zona de Risco' ? '⚠️ ' + t : t === 'LGPD' ? <Term k="LGPD">LGPD</Term> : t}
          </button>
        ))}
      </div>

      {/* Tab 0: Geral */}
      {tab === 0 && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div className="form-group"><label className="form-label">Nome do site</label><input type="text" className="form-input" value={siteName} onChange={e => setSiteName(e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Tagline</label><input type="text" className="form-input" value={tagline} onChange={e => setTagline(e.target.value)} /></div>
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', cursor: 'pointer', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={maintenance === 'true'} onChange={e => setMaintenance(String(e.target.checked))} style={{ accentColor: 'var(--petrol)', width: '16px', height: '16px' }} />
            Modo manutenção (redireciona não-admins)
          </label>
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', cursor: 'pointer', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={regOpen === 'true'} onChange={e => setRegOpen(String(e.target.checked))} style={{ accentColor: 'var(--petrol)', width: '16px', height: '16px' }} />
            Registro aberto (novos usuários podem se cadastrar)
          </label>
          <div className="form-group"><label className="form-label">Dias de teste gratuito (0 = sem teste)</label><input type="number" className="form-input" value={trialDays} onChange={e => setTrialDays(e.target.value)} min="0" style={{ maxWidth: '120px' }} /></div>
          <div className="form-group"><label className="form-label">Comissão por indicação paga (R$)</label><input type="number" className="form-input" value={affiliateCommission} onChange={e => setAffiliateCommission(e.target.value)} min="0" step="0.01" style={{ maxWidth: '120px' }} /></div>
          <SaveButton onClick={saveGeral} saving={saving} />
        </div>
      )}

      {/* Tab 1: Integrações */}
      {tab === 1 && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <SecretField label="Stripe Secret Key" value={stripeKey} onChange={v => setStripeKey(v)} />
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '-0.25rem', marginBottom: '0.5rem' }}>
            A chave secreta da Stripe é usada pelas Edge Functions para criar sessões de checkout, verificar assinaturas e processar reembolsos. Sem ela, nenhum pagamento por cartão ou PIX funcionará. Encontre em: Stripe Dashboard → Developers → API Keys → Secret key.
          </p>
          <p style={{ fontSize: 'var(--text-caption)', color: '#fbbf24', margin: '-0.25rem 0 0.5rem' }}>
            ⚠️ Nunca compartilhe esta chave. Em caso de vazamento, regenere imediatamente no painel da Stripe.
          </p>
          <div className="form-group"><label className="form-label">Google Tag Manager ID</label><input type="text" className="form-input" value={gtmId} onChange={e => setGtmId(e.target.value)} placeholder="GTM-XXXXXXX" style={{ fontFamily: 'monospace' }} /></div>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '-0.25rem', marginBottom: '0.5rem' }}>
            O GTM centraliza todos os scripts de rastreamento (Google Analytics, Facebook Pixel, etc.) sem precisar de novo deploy. Insira o ID para ativar coleta de dados de conversão, funil e e-commerce. Encontre em: tagmanager.google.com → Seu container → Container ID.
          </p>
          <SecretField label="Anthropic API Key" value={anthropicKey} onChange={v => setAnthropicKey(v)} />
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '-0.25rem', marginBottom: '0.5rem' }}>
            Usada para geração de conteúdo IA (mapas, previsões, tarot). Sem ela, todas as funcionalidades de IA param. Gerencie em: console.anthropic.com → API Keys.
          </p>
          <p style={{ fontSize: 'var(--text-caption)', color: '#fbbf24', margin: '-0.25rem 0 0.5rem' }}>
            ⚠️ Para maior segurança, prefira configurar esta chave em Supabase Dashboard → Edge Functions → Secrets.
          </p>
          <SecretField label="Resend API Key" value={resendKey} onChange={v => setResendKey(v)} />
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '-0.25rem', marginBottom: '0.5rem' }}>
            Usada para envio de e-mails transacionais (confirmação, redefinição de senha, notificações). Sem ela, nenhum e-mail é entregue. Encontre em: resend.com → API Keys.
          </p>
          <SaveButton onClick={saveIntegrations} saving={saving} />
        </div>
      )}

      {/* Tab 2: Notificações */}
      {tab === 2 && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div className="form-group"><label className="form-label">Email do admin</label><input type="email" className="form-input" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} /></div>
          {[
            { key: 'reg', label: 'Novo usuário cadastrado', val: notifyReg, set: setNotifyReg },
            { key: 'pay', label: 'Pagamento confirmado',    val: notifyPay, set: setNotifyPay },
            { key: 'churn', label: 'Risco de churn (30 dias sem atividade)', val: notifyChurn, set: setNotifyChurn },
          ].map(({ key, label, val, set }) => (
            <label key={key} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', cursor: 'pointer', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={val === 'true'} onChange={e => set(String(e.target.checked))} style={{ accentColor: 'var(--petrol)', width: '16px', height: '16px' }} />
              {label}
            </label>
          ))}
          <SaveButton onClick={saveNotifications} saving={saving} />
        </div>
      )}

      {/* Tab 3: LGPD */}
      {tab === 3 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="card">
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>✦ Status de conformidade</p>
            {['Política de Privacidade publicada', 'Termos de Uso publicados', 'LGPD aceito no cadastro', 'Dados criptografados em repouso (Supabase)', 'Portabilidade de dados implementada'].map(item => (
              <p key={item} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: '0.35rem 0' }}>
                <Check size={14} color="#4ade80" /> {item}
              </p>
            ))}
          </div>
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div className="form-group"><label className="form-label">Retenção de dados (dias, mín. 30)</label><input type="number" className="form-input" value={retentionDays} onChange={e => setRetentionDays(e.target.value)} min="30" style={{ maxWidth: '120px' }} /></div>
            <div className="form-group"><label className="form-label"><Term k="DPO">DPO</Term> (e-mail)</label><input type="email" className="form-input" value={dpoEmail} onChange={e => setDpoEmail(e.target.value)} /></div>
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', cursor: 'pointer', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={cookieBanner === 'true'} onChange={e => setCookieBanner(String(e.target.checked))} style={{ accentColor: 'var(--petrol)', width: '16px', height: '16px' }} />
              Exibir banner de cookies
            </label>
            <SaveButton onClick={saveLgpd} saving={saving} />
          </div>
          <div className="card">
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>✦ Exportar dados do usuário (Art. 18)</p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input type="email" className="form-input" value={exportEmail} onChange={e => setExportEmail(e.target.value)} placeholder="email@usuario.com" style={{ flex: 1 }} />
              <button className="btn btn-secondary btn-sm" onClick={exportUserData} disabled={!exportEmail || exporting}>{exporting ? 'Exportando...' : 'Exportar JSON'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Tab 4: Canais de Contato */}
      {tab === 4 && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.25rem' }}>
            ✦ Canais de Contato
          </p>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
            Esses valores alimentam a página pública /contato e o footer do site.
          </p>
          <div className="form-group">
            <label className="form-label">WhatsApp (apenas dígitos com DDI, ex: 5511999999999)</label>
            <input type="text" className="form-input" value={chWhatsapp} onChange={e => setChWhatsapp(e.target.value)} placeholder="5511999999999" style={{ fontFamily: 'monospace' }} />
          </div>
          <div className="form-group">
            <label className="form-label">Instagram (handle, sem @)</label>
            <input type="text" className="form-input" value={chInstagram} onChange={e => setChInstagram(e.target.value)} placeholder="professya" />
          </div>
          <div className="form-group">
            <label className="form-label">TikTok (handle, sem @)</label>
            <input type="text" className="form-input" value={chTiktok} onChange={e => setChTiktok(e.target.value)} placeholder="professya" />
          </div>
          <div className="form-group">
            <label className="form-label">YouTube (URL completa do canal)</label>
            <input type="url" className="form-input" value={chYoutube} onChange={e => setChYoutube(e.target.value)} placeholder="https://youtube.com/@professya" />
          </div>
          <div className="form-group">
            <label className="form-label">Email de suporte</label>
            <input type="email" className="form-input" value={chEmailSup} onChange={e => setChEmailSup(e.target.value)} placeholder="suporte@professya.com" />
          </div>
          <SaveButton onClick={saveChannels} saving={saving} />
        </div>
      )}

      {/* Tab 6: Interface */}
      {tab === 6 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.25rem' }}>
              ✦ Ordem das seções do menu admin
            </p>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0 0 0.5rem' }}>
              Reordene as seções do painel admin usando os botões ↑ e ↓. A ordem é salva e aplicada globalmente a todos os administradores.
            </p>
            {!navOrderLoaded ? (
              <div className="shimmer" style={{ height: '200px', borderRadius: 'var(--radius-md)' }} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {navOrder.map((section, i) => (
                  <div key={section} style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: 'var(--bg-base)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 'var(--radius-sm)',
                  }}>
                    <span style={{ flex: 1, fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 600 }}>
                      {section}
                    </span>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => moveNavSection(i, 'up')}
                      disabled={i === 0}
                      style={{ padding: '2px 8px', opacity: i === 0 ? 0.3 : 1 }}
                      title="Mover para cima"
                    >
                      ↑
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => moveNavSection(i, 'down')}
                      disabled={i === navOrder.length - 1}
                      style={{ padding: '2px 8px', opacity: i === navOrder.length - 1 ? 0.3 : 1 }}
                      title="Mover para baixo"
                    >
                      ↓
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <SaveButton onClick={saveNavOrder} saving={savingNavOrder} />
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setNavOrder(DEFAULT_NAV_ORDER)}
                title="Restaurar ordem padrão"
              >
                Restaurar padrão
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab 5: Zona de Risco */}
      {tab === 5 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{padding:'0.75rem 1rem', backgroundColor:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'6px', marginBottom:'1rem', fontSize:'var(--text-caption)', color:'#f87171'}}>
            🔴 <strong>Zona de Risco:</strong> As ações abaixo são irreversíveis ou afetam todos os usuários imediatamente. Certifique-se de que realmente deseja executá-las.
          </div>
          {[
            {
              title: 'Limpar cache de previsões públicas',
              desc: 'Remove todas as previsões cacheadas em platform_settings.',
              confirm: '', action: clearForecastCache, label: 'Limpar cache', btnClass: 'btn-secondary',
            },
            {
              title: 'Desativar todos os cupons',
              desc: 'Define active=false em todos os cupons.',
              confirm: 'DESATIVAR', action: async () => { await supabase.from('discount_coupons').update({ active: false }).neq('id', ''); }, label: 'Desativar cupons', btnClass: 'btn-danger',
            },
            {
              title: 'Resetar todos os preços para gratuito',
              desc: 'Define price_brl=0 para todos os produtos. IRREVERSÍVEL sem backup.',
              confirm: 'RESETAR', action: resetAllPrices, label: 'Resetar preços', btnClass: 'btn-danger',
            },
          ].map(({ title, desc, confirm, action, label, btnClass }) => (
            <div key={title} className="card" style={{ border: confirm ? '1px solid rgba(239,68,68,0.3)' : '1px solid var(--border-subtle)' }}>
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>{title}</p>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '0.75rem' }}>{desc}</p>
              {confirm && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <input type="text" className="form-input" value={dangerConfirm === title ? resetInput : ''} onChange={e => { setDangerConfirm(title); setResetInput(e.target.value); }} placeholder={`Digite "${confirm}" para confirmar`} style={{ maxWidth: '280px' }} />
                </div>
              )}
              <button className={`btn ${btnClass} btn-sm`} onClick={action} disabled={confirm ? (dangerConfirm !== title || resetInput !== confirm) : false}>
                {label}
              </button>
            </div>
          ))}

          <div className="card">
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>Criar/resetar usuário de teste</p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '0.75rem' }}>
              test@professya.com / Professya@Test2026! com acesso Pro e 10 créditos de cada tipo.
            </p>
            <button className="btn btn-secondary btn-sm" onClick={async () => {
              const { data } = await supabase.functions.invoke('create-test-user', {});
              if (data?.data?.email) alert(`✓ Usuário criado: ${data.data.email}`);
            }}>
              Criar usuário de teste
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
